import { compact, keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import {
  buildCompositeAssetValues,
  distributeAssetOptionValues,
  useEntityAssetOptions,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useEntityAssetOptions';
import { SplitDispositionFormShape } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/SplitDispositionModal/SplitDispositionModal.types';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';

interface BdgSplitAssetSelectorProps {
  entityIdOverride?: string;
}

/**
 * Asset multi-select for the beneficiary-designations split modal. Same as the
 * DP `SplitAssetSelector` but reads the entity id from the entity-details
 * context (or an override) rather than the DispositiveProvisionsContext.
 */
export function BdgSplitAssetSelector({
  entityIdOverride,
}: BdgSplitAssetSelectorProps) {
  const { entityId: contextEntityId } = useEntityDetailsContext();
  const entityId = entityIdOverride ?? contextEntityId;
  const { control, setValue } = useFormContext<SplitDispositionFormShape>();
  const { options, loading } = useEntityAssetOptions(entityId);

  const [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds] =
    useWatch({
      control,
      name: [
        'assetStaticIds',
        'assetGroupIds',
        'insurancePolicyIds',
        'ownershipStakeIds',
      ],
    });

  const compositeValues = useMemo(
    () =>
      buildCompositeAssetValues({
        assetStaticIds,
        assetGroupIds,
        insurancePolicyIds,
        ownershipStakeIds,
      }),
    [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds]
  );

  const selectedOptions = useMemo(() => {
    const optionsByValue = keyBy(options, 'value');
    return compact(compositeValues.map((v) => optionsByValue[v]));
  }, [compositeValues, options]);

  const handleChange = useCallback(
    (_event: unknown, newValues: Array<{ value: string }>) => {
      const distributed = distributeAssetOptionValues(
        newValues.map((v) => v.value)
      );

      setValue('assetStaticIds', distributed.assetStaticIds, {
        shouldDirty: true,
      });
      setValue('assetGroupIds', distributed.assetGroupIds, {
        shouldDirty: true,
      });
      setValue('insurancePolicyIds', distributed.insurancePolicyIds, {
        shouldDirty: true,
      });
      setValue('ownershipStakeIds', distributed.ownershipStakeIds, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const hasNoAssets = !loading && options.length === 0;
  const [inputValue, setInputValue] = useState('');

  return (
    <TypeaheadMultiSelectInput
      label="Assets"
      options={options}
      values={selectedOptions}
      onChange={handleChange}
      loading={loading}
      placeholder={hasNoAssets ? 'No assets available' : 'Select assets'}
      disabled={hasNoAssets}
      inputValue={inputValue}
      onInputChange={(value) => setInputValue(value)}
    />
  );
}
